import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Sparkles, CheckCircle2, Activity, ArrowLeft, Camera, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import DashboardLayout from '../components/DashboardLayout';
import AssessmentUploadView from '../components/AssessmentUploadView';
import AssessmentLiveView from '../components/AssessmentLiveView';
import MobilitySilhouette from '../components/MobilitySilhouette';
import api from '../services/api';
import { toast } from 'sonner';

/**
 * /assessment — Mobility & ROM Baseline.
 *
 * Iterates through the catalog one test at a time. Each test offers TWO capture
 * modes that feed the SAME scoring + submit pipeline:
 *   • LIVE (default): full-screen camera + skeleton, hold-to-capture.
 *   • UPLOAD: film a clip yourself, analysed in-browser.
 *
 * NO MANUAL ENTRY — the platform refuses guessed numbers.
 */
const Assessment = () => {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [active, setActive] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [finalDoc, setFinalDoc] = useState(null);
  const [mode, setMode] = useState('live'); // 'live' | 'upload' — live is the default

  const fetchCatalog = useCallback(async () => {
    try {
      const [c, start] = await Promise.all([
        api.getMovementBaselineCatalog(),
        api.startBaselineAssessment(),
      ]);
      setCatalog(c.data);
      setActive(start.data);
      const tested = new Set((start.data?.test_results || []).map(r => r.test_id));
      const firstUntested = (c.data?.tests || []).findIndex(t => !tested.has(t.id));
      setStepIdx(firstUntested === -1 ? 0 : firstUntested);
    } catch (e) {
      toast.error('Failed to start assessment');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCatalog(); }, [fetchCatalog]);

  const tests = catalog?.tests || [];
  const test = tests[stepIdx];

  const handleTestComplete = useCallback(async ({ test_id, measurements }) => {
    if (!measurements?.length) {
      toast.error('No measurements captured');
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.submitBaselineTest(test_id, measurements);
      setActive(r.data);
      if (r.data?.completed) {
        setCompleted(true);
        setFinalDoc(r.data);
      } else if (stepIdx + 1 < tests.length) {
        setStepIdx(stepIdx + 1);
      }
    } catch (e) {
      toast.error('Could not save that result');
    } finally {
      setSubmitting(false);
    }
  }, [stepIdx, tests.length]);

  if (loading || !test) {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <Activity className="w-12 h-12 text-primary animate-pulse" />
        </div>
      </DashboardLayout>
    );
  }

  // Final results
  if (completed && finalDoc) {
    const regionScores = finalDoc.region_scores || {};
    const overall = finalDoc.overall_mobility || 0;
    const asymmetry = finalDoc.asymmetry_flags || [];
    return (
      <DashboardLayout>
        <div className="p-6 max-w-4xl mx-auto space-y-6">
          <div className="text-center">
            <CheckCircle2 className="w-14 h-14 text-primary mx-auto mb-3" />
            <h1 className="font-secondary font-bold text-3xl tracking-tight">MOBILITY MAPPED</h1>
            <p className="text-zinc-400 mt-2">Your mobility baseline is saved — Nikki will tune your plan to it.</p>
          </div>

          <div className="hpme-card p-6 text-center" data-testid="baseline-overall">
            <div className="hpme-card-corner" />
            <p className="hpme-label mb-2">OVERALL MOBILITY</p>
            <p className="font-secondary font-bold text-6xl text-primary tracking-tight">{overall}</p>
            <p className="text-xs text-zinc-500 mt-1">out of 100 · camera-tracked clinical band</p>
          </div>

          <div className="hpme-card p-6">
            <div className="hpme-card-corner" />
            <p className="hpme-label mb-3">YOUR MOBILITY MAP</p>
            <div className="flex justify-center">
              <MobilitySilhouette regionScores={regionScores} size="lg" showLabels />
            </div>
          </div>

          {asymmetry.length > 0 && (
            <div className="hpme-card p-6 border-amber-500/30">
              <div className="hpme-card-corner" />
              <p className="hpme-label mb-3 text-amber-300">ASYMMETRY FLAGS · L vs R</p>
              <div className="space-y-2">
                {asymmetry.map(f => (
                  <div key={f.test_id} className="flex items-center justify-between text-sm py-2 px-3 rounded-sm bg-amber-500/10 border border-amber-500/30" data-testid={`asymmetry-${f.test_id}`}>
                    <span className="text-amber-100">{f.test_name}</span>
                    <span className="font-secondary font-bold text-amber-200">
                      L {f.left_score} · R {f.right_score} <span className="text-amber-400">({f.delta} gap, weaker {f.weaker_side})</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 justify-center flex-wrap">
            <Button className="hpme-btn-primary" onClick={() => navigate('/mobility')} data-testid="baseline-go-mobility">
              Open Mobility Map <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
            <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="baseline-go-dashboard">
              Dashboard
            </Button>
            <Button variant="outline" onClick={() => navigate('/chat')} data-testid="baseline-go-chat">
              Talk to Nikki
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Per-test screen
  const total = tests.length;
  const progressPct = Math.round((stepIdx / total) * 100);

  return (
    <DashboardLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-5" data-testid="assessment-page">
        <div>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-zinc-500 hover:text-white text-sm flex items-center gap-1 mb-3"
            data-testid="assessment-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Cancel &amp; return
          </button>
          <div className="flex items-center justify-between mb-2">
            <span className="hpme-label">Test {stepIdx + 1} of {total} · {submitting ? 'Saving…' : (mode === 'live' ? 'Live camera' : 'Upload a clip')}</span>
            <span className="text-xs text-zinc-500">{progressPct}%</span>
          </div>
          <div className="h-1 bg-zinc-800 rounded">
            <div className="h-full bg-primary rounded transition-all" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        {/* Capture-mode toggle — both feed the SAME scoring pipeline. */}
        <div className="flex bg-zinc-950 rounded-sm p-0.5 border border-zinc-800 w-fit" data-testid="assessment-mode-toggle">
          <button
            type="button"
            onClick={() => setMode('live')}
            className={`text-xs font-bold tracking-wider uppercase px-3 py-1.5 rounded-sm flex items-center gap-1.5 transition-colors ${mode === 'live' ? 'bg-primary text-black' : 'text-zinc-400 hover:text-white'}`}
            data-testid="assessment-mode-live"
          >
            <Camera className="w-3.5 h-3.5" /> Live camera
          </button>
          <button
            type="button"
            onClick={() => setMode('upload')}
            className={`text-xs font-bold tracking-wider uppercase px-3 py-1.5 rounded-sm flex items-center gap-1.5 transition-colors ${mode === 'upload' ? 'bg-primary text-black' : 'text-zinc-400 hover:text-white'}`}
            data-testid="assessment-mode-upload"
          >
            <Upload className="w-3.5 h-3.5" /> Upload clip
          </button>
        </div>

        {mode === 'live' ? (
          <AssessmentLiveView
            key={`live-${test.id}`}
            testDef={test}
            onComplete={handleTestComplete}
            onUseUpload={() => setMode('upload')}
          />
        ) : (
          <AssessmentUploadView
            key={`upload-${test.id}`}
            testDef={test}
            onComplete={handleTestComplete}
          />
        )}

        <p className="text-[11px] text-zinc-600 text-center flex items-center justify-center gap-1">
          <Sparkles className="w-3 h-3" /> Analyzed privately on your device — a smoothed, held-peak angle, no guesswork.
        </p>
      </div>
    </DashboardLayout>
  );
};

export default Assessment;
